import { graphql, useStaticQuery } from 'gatsby';
import React, { Fragment, memo, useEffect, useState } from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';
import { useRandInt } from '../../hooks';
import Logo from '../../images/logo_signet.svg';
import LogoText from '../../images/logo_text.svg';
import { styleMods, unwrap } from '../../utils';
import { cmpByKey } from '../../utils/cmp';
import * as iter from '../../utils/iter';
import ImageGallery from '../image-gallery.js';
import IntlString from '../intl-string.js';
import Link from '../link';
import StripedBox from '../striped-box';
import style from './homepage-intro.module.scss';

export default memo(
	/**
	 * @param {{
	 *   entries: Array<import("../../../tmp/graphql-types").PageElementHomepageIntroFragment>,
	 *   hashState: number,
	 * }} props
	 */
	function HomepageIntro({entries, hashState}) {
		/** @type {import("../../../tmp/graphql-types").PageElementHomepageIntroComponentQuery} */
		const data = useStaticQuery(graphql `
			fragment PageElementHomepageIntro on PageElementHomepageIntroEntry {
				category {
					...IntlString
				}
				text {
					...IntlString
				}
				url
				datetime
			}

			query PageElementHomepageIntroComponent {
				allArtistsJson(sort: {order: ASC, fields: order}) {
					nodes {
						homepageImage {
							...ImageGallery
						}
					}
				}

				site {
					buildTime
				}
			}
		`);

		const [currDatetime, setCurrDatetime] = useState(
			// unwrap should be safe here...
			() => new Date(unwrap(data.site?.buildTime))
		);

		useEffect(
			() => {
				setCurrDatetime(new Date());
			},
			[],
		);

		const images = iter.into(data.allArtistsJson.nodes)
			.map((art) => art.homepageImage)
			.filter(Boolean)
			.map(unwrap)
			.toArray();

		const artIndex = useRandInt(0, images.length);

		const stripedBoxes = (
			<Fragment>
				<StripedBox
					animation
					className={styleMods(style, 'stripedBox', 'logoAligned')}
					style={{
						height: '79%',
					}}
					color="#ff3714"
				/>
				<StripedBox
					animation
					className={style.stripedBox}
					style={{
						left: '30%',
						top: '10%',
						height: '79%',
					}}
					color="#d500d5"
				/>
				<StripedBox
					animation
					className={style.stripedBox}
					style={{
						left: '10%',
						top: '50%',
						height: '50%',
					}}
					color="#e8e800"
				/>
			</Fragment>
		);

		const entriesSorted = iter.into(entries)
			.map((e) => ({
				...e,
				datetime: new Date(e.datetime * 1000),
			}))
			.filter((e) => currDatetime < e.datetime)
			.toArray();
		entriesSorted.sort(cmpByKey((e) => e.datetime));

		const entriesContent = (
			<ol className={style.entries}>
				{iter.into(entriesSorted)
					.take(3)
					.asIndexedPairs()
					.map(([index, entry]) => {
						const content = (
							<Fragment>
								<div className={style.entryCategory}>
									<IntlString {...entry.category} />
								</div>

								<div className={style.entryText}>
									<IntlString {...entry.text} />
								</div>

								<div className={style.entryDatetime}>
									<FormattedDate
										value={entry.datetime}
										timeZone="Europe/Berlin"
										// weekday="short"
										month="short"
										day="2-digit"
									/>
									{" | "}
									<FormattedTime
										value={entry.datetime}
										timeZone="Europe/Berlin"
										hour="2-digit"
										minute="2-digit"
									/>
								</div>
							</Fragment>
						);

						const content2 = (() => {
							if (entry.url) {
								return (
									<Link href={entry.url} className={style.entryLink}>
										{content}
									</Link>
								);
							} else {
								return content;
							}
						})();

						return (
							<li key={index} className={style.entry}>
								{content2}
							</li>
						);
					})
					.toArray()
				}
			</ol>
		);

		return (
			<div className={style.wrapperOuter}>
				<div className={style.wrapperInner}>
					<div className={style.base}>
						{stripedBoxes}

						<div className={style.bsLogoWrapper}>
							<div className={style.bsLogoBg1} />
							<div className={style.bsLogoBg2} />
							<Logo className={style.bsLogo} />
							<LogoText className={style.bsLogoText} />
						</div>

						{artIndex !== null && (
							<div className={style.artwork}>
								<ImageGallery
									imageStartIndex={artIndex}
									images={images}
								/>
							</div>
						)}
					</div>
				</div>

				{entriesContent}
			</div>
		);
	}
);
